import { formatMoney, formatValidityDuration } from '@/lib/format';
import type { AddOn, Pass } from '@/lib/passes-types';
import { Icon } from '@/components/ui/icon';
import { Money } from '@/components/ui/money';
import { Stepper } from '@/components/ui/stepper';
import { Toggle } from '@/components/ui/toggle';

/** A selectable bookable entry-pass (catalog item). Mirrors the metro FareOptionCard. */
export function PassOptionCard({
  pass,
  selected,
  onSelect,
}: {
  pass: Pass;
  selected: boolean;
  onSelect: () => void;
}) {
  const thumb = pass.images?.[0]?.url;
  const validity = formatValidityDuration(pass.validityDuration);
  const subtitle = [pass.shortDesc, validity ? `Valid ${validity}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-checked={selected}
      className={`wb-option ${selected ? 'is-selected' : ''}`}
    >
      {thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          style={{
            width: 46,
            height: 46,
            flexShrink: 0,
            borderRadius: 14,
            objectFit: 'cover',
          }}
        />
      ) : (
        <span className="wb-leadicon cat-pass" style={{ width: 46, height: 46 }}>
          <Icon name="ticket" size={22} />
        </span>
      )}
      <span className="wb-grow" style={{ minWidth: 0 }}>
        <span
          className="wb-strong"
          style={{
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {pass.name}
        </span>
        {subtitle && (
          <span
            className="wb-tiny"
            style={{
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
      <Money v={formatMoney(pass.price).replace('₹', '')} strong />
    </button>
  );
}

/** +/- quantity stepper (1..max). */
export function QuantityStepper({
  value,
  max,
  onChange,
}: {
  value: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="wb-row wb-gap-8">
      <span className="wb-tiny">Qty</span>
      <Stepper value={value} min={1} max={Math.max(1, max || 6)} onChange={onChange} />
    </div>
  );
}

/** Optional add-on toggles (e.g. audio guide). */
export function AddOnToggles({
  addOns,
  selected,
  onToggle,
}: {
  addOns: AddOn[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  if (addOns.length === 0) return null;
  return (
    <div className="wb-col wb-gap-8">
      <p className="wb-label" style={{ margin: 0 }}>
        Add-ons
      </p>
      {addOns.map((a) => (
        <div key={a.id} className="wb-option" style={{ cursor: 'default' }}>
          <span className="wb-grow wb-strong" style={{ fontSize: 15 }}>
            {a.name || 'Add-on'}
          </span>
          <Money v={formatMoney(a.price).replace('₹', '')} className="wb-ink2" />
          <Toggle
            checked={selected.includes(a.id)}
            onChange={() => onToggle(a.id)}
            label={a.name || 'Add-on'}
          />
        </div>
      ))}
    </div>
  );
}
